// hooks
import { useAxios } from '@/vue3/hooks/axiosHook';
import { useStore } from '@/store';

// models
import { CourseSchema, PublicFacingLinkSummary } from '@/vue3/api/models';

export const usePublicInfoService = () => {
    // hooks
    const store = useStore();

    const axios = useAxios();
    const { apiUrl } = store.state;

    const pathSegments = window.location.pathname.split('/').filter(Boolean);
    const publicCoursePathIndex = pathSegments.findIndex(
        (segment, index) =>
            segment === 'public' && pathSegments[index + 1] === 'course',
    );
    const publicCoursePathSegmentsRaw = pathSegments.slice(
        publicCoursePathIndex + 2,
    );
    const publicCoursePathSegments =
        publicCoursePathSegmentsRaw[0] === 'share-link'
            ? publicCoursePathSegmentsRaw.slice(1)
            : publicCoursePathSegmentsRaw;
    let resolvedPublicLink: Promise<PublicFacingLinkSummary> | null = null;

    const getPublicLink = async (): Promise<PublicFacingLinkSummary> => {
        if (publicCoursePathSegments.length === 1) {
            return {
                id: 0,
                name: '',
                uid: decodeURIComponent(publicCoursePathSegments[0]),
            };
        }

        if (publicCoursePathSegments.length < 2) {
            throw new Error('Invalid public course URL');
        }

        if (!resolvedPublicLink) {
            const courseName = decodeURIComponent(publicCoursePathSegments[0]);
            const linkName = decodeURIComponent(publicCoursePathSegments[1]);

            resolvedPublicLink = axios
                .get(
                    `${apiUrl}/public/course/share-link/${encodeURIComponent(
                        courseName,
                    )}/${encodeURIComponent(linkName)}`,
                    0,
                    { bypassAuthCheck: true },
                )
                .then(response => response.data as PublicFacingLinkSummary);
        }

        return resolvedPublicLink!;
    };

    const getPublicUid = async (): Promise<string> => {
        const publicLink = await getPublicLink();
        return publicLink.uid;
    };

    const getPublicLinkName = async (): Promise<string> => {
        const publicLink = await getPublicLink();
        return publicLink.name?.trim() || '';
    };

    // methods
    const updatePublicLink = async (
        uid: string,
        name: string,
        courseId: number,
    ): Promise<void> => {
        try {
            await axios.put(
                `${apiUrl}/public/course/share-links/${courseId}/${uid}`,
                { name },
            );
        } catch (error) {
            console.error('Failed to update public link');
            throw error;
        }
    };

    const deletePublicLink = async (linkId: number): Promise<boolean> => {
        try {
            await axios.deleteId(
                `${apiUrl}/public/course/share-link/${linkId}`,
            );

            return true;
        } catch (error) {
            console.error('Failed to delete public link');
            throw error;
        }
    };

    const createPublicLink = async (
        courseId: number,
        name: string,
    ): Promise<PublicFacingLinkSummary> => {
        try {
            const response = await axios.post(
                `${apiUrl}/course/share-link/${courseId}`,
                { name },
            );

            return response.data;
            // return {} as PublicFacingLinkSummary;
        } catch (error) {
            console.error('Failed to create public link');
            throw error;
        }
    };

    const getPublicLinks = async (
        courseId: number,
    ): Promise<PublicFacingLinkSummary[]> => {
        try {
            const response = await axios.get(
                `${apiUrl}/public/course/share-links/${courseId}`,
                0,
                { bypassAuthCheck: true },
            );
            return response.data;
        } catch (error) {
            console.error('Failed to fetch public links');
            throw error;
        }
    };

    const getPublicLinkGeoRecordIds = async (
        linkId: number,
    ): Promise<number[]> => {
        const response = await axios.get(
            `${apiUrl}/course/share-link/${linkId}/geo-record-ids`,
        );

        return response.data?.geoRecordIds || [];
    };

    const updatePublicLinkGeoRecordIds = async (
        linkId: number,
        courseSchemaId: number,
        geoRecordIds: number[],
    ): Promise<number[]> => {
        const response = await axios.put(
            `${apiUrl}/course/share-link/${linkId}/geo-record-ids`,
            {
                courseSchemaId,
                geoRecordIds,
            },
        );

        return response.data.geoRecordIds;
    };

    const getPublicCourseSchema = async (): Promise<CourseSchema> => {
        const uid = await getPublicUid();
        const res = await axios.get(
            `${apiUrl}/public/course-schema/${uid}/list`,
            0,
            { bypassAuthCheck: true },
        );
        return res.data;
    };

    const getPublicCourseExtent = async () => {
        try {
            const uid = await getPublicUid();
            const response = await axios.get(
                `${apiUrl}/public/course/${uid}/extent`,
                0,
                { bypassAuthCheck: true },
            );
            return response.data;
        } catch (error) {
            console.error('Invalid');
            return null;
        }
    };

    const getPublicAllGeoRecordsAsTree = async (): Promise<any> => {
        const uid = await getPublicUid();
        const res = await axios.get(
            `${apiUrl}/public/geo-record/tree/temp/course-schema/${uid}`,
            0,
            { bypassAuthCheck: true },
        );
        return res.data;
    };

    const getPublicAllMeasureRecords = async () => {
        const uid = await getPublicUid();
        const res = await axios.get(
            `${apiUrl}/public/measure-record/course-schema/${uid}`,
            0,
            { bypassAuthCheck: true },
        );
        return res.data;
    };

    const getPublicRenderSelection = async (): Promise<any> => {
        const payload = {
            id: 0,
            account_id: 0,
            courseSchemaId: 0,
            visibleLayers: [],
            visibleLayersKeys: null,
            visibleRecords: [],
            measureRecords: false,
            mosaic: '',
            mapStyle: '',
            threeD: false,
            hillshade: false,
            permanentLayers: null,
        };

        return new Promise(function (resolve, reject) {
            resolve(payload);
        });
    };

    const getPublicDefaultStyle = async (mapStyle: string) => {
        const uid = await getPublicUid();
        const res = await axios.get(
            `${apiUrl}/public/mapbox/style/course/${uid}/${mapStyle}.json`,
            0,
            { bypassAuthCheck: true },
        );

        return res.data;
    };

    const getPublicGeoRecord = async (geoRecordId: number): Promise<any> => {
        const uid = await getPublicUid();
        const res = await axios.get(
            `${apiUrl}/public/geo-record/${uid}/${geoRecordId}`,
            0,
            { bypassAuthCheck: true },
        );
        return res.data;
    };

    const getPublicGeoRecordPerimeter = async (id: number): Promise<any> => {
        const uid = await getPublicUid();
        const res = await axios.get(
            `${apiUrl}/public/geometry-perimeter/${uid}/${id}`,
            0,
            { bypassAuthCheck: true },
        );
        return res.data;
    };

    const getPublicAllIconFiles = async () => {
        const uid = await getPublicUid();
        const res = await axios.get(
            `${apiUrl}/public/geo-record-category/all/icons/${uid}`,
            0,
            { bypassAuthCheck: true },
        );
        return res.data;
        // const payload = [];
        // return new Promise(function(resolve, reject) {
        //     resolve(payload);
        //   });
    };

    const getPublicMapboxLayerTree = async () => {
        const uid = await getPublicUid();
        const res = await axios.get(
            `${apiUrl}/public/mapbox/layer/tree/${uid}`,
            0,
            { bypassAuthCheck: true },
        );

        return res.data;
    };

    const getPublicAllMosaics = async () => {
        const uid = await getPublicUid();
        const res = await axios.get(
            `${apiUrl}/public/mapbox/mosaic/course/${uid}`,
            0,
            { bypassAuthCheck: true },
        );

        return res.data;
    };

    const getPublicAllCourseCoords = async () => {
        const uid = await getPublicUid();
        const res = await axios.get(
            `${apiUrl}/public/mapbox/course/coordinates/${uid}`,
            0,
            { bypassAuthCheck: true },
        );

        return res.data;
    };

    const fetchPublicCustomLayerData = async (): Promise<any> => {
        const payload = [];
        return new Promise(function (resolve, reject) {
            resolve(payload);
        });
    };

    return {
        getPublicGeoRecord,
        getPublicGeoRecordPerimeter,
        getPublicCourseSchema,
        getPublicAllGeoRecordsAsTree,
        getPublicCourseExtent,
        getPublicDefaultStyle,
        getPublicRenderSelection,
        getPublicAllMeasureRecords,
        getPublicAllIconFiles,
        getPublicMapboxLayerTree,
        getPublicAllMosaics,
        getPublicAllCourseCoords,
        fetchPublicCustomLayerData,
        createPublicLink,
        getPublicLinks,
        getPublicLinkGeoRecordIds,
        updatePublicLinkGeoRecordIds,
        updatePublicLink,
        deletePublicLink,
        getPublicLinkName,
    };
};
